import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { profile } from '@gonfalon/bootstrap-data/bootstrapData';
import { toHref, toVegaConversation } from '@gonfalon/navigator/navigators';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';

import { trackVegaAiConversationOpenInObservability } from '../../vegaAnalytics';
import { canShowVegaO11yConversationLinks } from '../canShowVegaO11yConversationLinks';
import { encodeConversationRelatedResource, RELATED_RESOURCE_PARAM } from '../toObservabilityConversation';

export const VIEW_TRACES_MENU_ID = 'view-traces';
export const VIEW_TRACES_LABEL = 'View traces';

/**
 * Staff-only jump from a Vega chat into the observability conversation panel
 * on the current page. Hidden from customers and on unsaved (`new`) chats.
 */
export function shouldShowVegaO11yConversationLink(conversationId: string | null | undefined): boolean {
  return Boolean(conversationId && conversationId !== 'new' && canShowVegaO11yConversationLinks(profile()?.email));
}

const VEGA_SEGMENT_RE = /\/projects\/[^/]+\/vega(?:\/|$)/;

export function useOpenVegaO11yConversation() {
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectKey } = useProjectId();

  return useCallback(
    (conversationId: string) => {
      const encoded = encodeConversationRelatedResource(conversationId);

      if (VEGA_SEGMENT_RE.test(location.pathname)) {
        // On the full-page Vega layout RelatedResourcePanel is already
        // mounted, so setting the param in place slides the panel open
        // without a navigation.
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set(RELATED_RESOURCE_PARAM, encoded);
            return next;
          },
          { replace: true, preventScrollReset: true },
        );
      } else {
        // On product pages (flags, contexts, …) the global Vega side
        // panel does not mount RelatedResourcePanel. Navigate to the
        // full Vega page where it is always available.
        navigate(
          toHref(toVegaConversation({ projectKey, conversationId }, { search: { [RELATED_RESOURCE_PARAM]: encoded } })),
        );
      }

      trackVegaAiConversationOpenInObservability(conversationId);
    },
    [setSearchParams, navigate, location.pathname, projectKey],
  );
}
